import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  useMediaQuery,
  Card,
  CardActionArea,
  CardContent,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  LocalGasStation as GasIcon,
  Refresh as RefreshIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { plantsApi } from '../../services/api';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';

// Componente per visualizzare il livello del serbatoio
const LevelIndicator = ({ percent, threshold }) => {
  const isLow = percent <= threshold;
  const color = isLow ? 'error' : percent <= 30 ? 'warning' : 'success';
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(percent, 100)}
          color={color}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 45, fontWeight: 'bold', color: `${color}.main` }}>
        {percent.toFixed(0)}%
      </Typography>
      {isLow && (
        <Tooltip title="Livello sotto la soglia di allarme">
          <WarningIcon color="error" fontSize="small" />
        </Tooltip>
      )}
    </Box>
  );
};

// Toolbar personalizzata
function CustomToolbar({ onRefresh }) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Button startIcon={<RefreshIcon />} onClick={onRefresh} size="small">
        Aggiorna
      </Button>
    </GridToolbarContainer>
  );
}

const PlantList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Stati
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([{ field: 'plant_code', sort: 'asc' }]);
  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterLowLevel, setFilterLowLevel] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plant: null });
  const [createDialog, setCreateDialog] = useState(false);
  const [newPlant, setNewPlant] = useState({
    plant_code: '',
    delivery_code: '',
    nome: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    capacita_totale: '',
    current_level_liters: '',
    low_level_threshold: 15,
  });

  // Query per lista impianti
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['plants', paginationModel, sortModel, searchText, filterActive, filterLowLevel],
    queryFn: async () => {
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        sortField: sortModel[0]?.field || 'plant_code',
        sortOrder: sortModel[0]?.sort || 'asc',
      };
      
      if (searchText) params.search = searchText;
      if (filterActive !== 'all') params.attivo = filterActive === 'active';
      if (filterLowLevel) params.lowLevel = 'true';
      
      const response = await plantsApi.getAll(params);
      return response.data;
    },
    placeholderData: (prev) => prev,
  });
  // Mutation per creare impianto
  const createMutation = useMutation({
    mutationFn: (data) => plantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['plants']);
      setCreateDialog(false);
      setNewPlant({
        plant_code: '',
        delivery_code: '',
        nome: '',
        indirizzo: '',
        cap: '',
        citta: '',
        provincia: '',
        capacita_totale: '',
        current_level_liters: '',
        low_level_threshold: 15,
      });
    },
  });

  // Mutation per eliminare impianto
  const deleteMutation = useMutation({
    mutationFn: (id) => plantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['plants']);
      setDeleteDialog({ open: false, plant: null });
    },
  });

  // Colonne DataGrid
  const columns = [
    {
      field: 'plant_code',
      headerName: 'Cod. Impianto',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      ),
    },
    {
      field: 'delivery_code',
      headerName: 'Cod. Consegna',
      width: 130,
    },
    {
      field: 'nome',
      headerName: 'Nome',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'indirizzo',
      headerName: 'Indirizzo',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const plant = params.row;
        const fullAddress = [plant.indirizzo, plant.cap, plant.citta, plant.provincia].filter(Boolean).join(', ');
        return fullAddress || '-';
      },
    },
    {
      field: 'capacita_totale',
      headerName: 'Capacità',
      width: 100,
      renderCell: (params) => params.value ? `${params.value} L` : '-',
    },
    {
      field: 'current_level_percent',
      headerName: 'Livello Stimato',
      width: 180,
      renderCell: (params) => {
        // Usa il livello stimato se disponibile, altrimenti il livello salvato
        const estimatedPercent = params.row.estimated_level_percent;
        const percent = estimatedPercent !== null && estimatedPercent !== undefined 
          ? parseFloat(estimatedPercent) 
          : parseFloat(params.value) || 0;
        const threshold = parseFloat(params.row.low_level_threshold) || 15;
        return <LevelIndicator percent={percent} threshold={threshold} />;
      },
    },
    {
      field: 'current_level_liters',
      headerName: 'Litri Stimati',
      width: 100,
      renderCell: (params) => {
        // Usa i litri stimati se disponibili, altrimenti quelli salvati
        const estimatedLiters = params.row.estimated_level_liters;
        const liters = estimatedLiters !== null && estimatedLiters !== undefined 
          ? parseFloat(estimatedLiters) 
          : parseFloat(params.value) || 0;
        return `${liters.toFixed(0)} L`;
      },
    },
    {
      field: 'tank',
      headerName: 'Serbatoio',
      width: 130,
      sortable: false,
      renderCell: (params) => params.value?.matricola || '-',
    },
    {
      field: 'azienda',
      headerName: 'Azienda',
      width: 150,
      valueGetter: (params) => params.row.azienda?.company_name || '-',
    },
    {
      field: 'attivo',
      headerName: 'Stato',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Attivo' : 'Inattivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Visualizza">
            <IconButton
              size="small"
              onClick={() => navigate(`/crm/impianti/${params.row.id}`)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifica">
            <IconButton
              size="small"
              onClick={() => navigate(`/crm/impianti/${params.row.id}/modifica`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteDialog({ open: true, plant: params.row })}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const plants = data?.data || [];
  const totalRows = data?.pagination?.total || 0;

  return (
    <Box sx={{ p: { xs: 0, sm: 1, md: 3 } }}>
      {/* Header */}
      <ResponsiveHeader
        title="Impianti"
        subtitle="Gestione impianti GPL con monitoraggio livelli"
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Impianto',
          icon: <AddIcon />,
          onClick: () => setCreateDialog(true),
        }}
      />

      {/* Filtri */}
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
        <ResponsiveFilters
          searchValue={searchText}
          onSearchChange={setSearchText}
          searchPlaceholder="Cerca per codice, nome, indirizzo..."
          activeFilterCount={(filterActive !== 'all' ? 1 : 0) + (filterLowLevel ? 1 : 0)}
          onClearFilters={() => { setFilterActive('all'); setFilterLowLevel(false); }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Stato</InputLabel>
            <Select
              value={filterActive}
              label="Stato"
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="active">Solo attivi</MenuItem>
              <MenuItem value="inactive">Solo inattivi</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant={filterLowLevel ? 'contained' : 'outlined'}
            color="warning"
            startIcon={<WarningIcon />}
            onClick={() => setFilterLowLevel(!filterLowLevel)}
            fullWidth
            size="small"
          >
            Livello Basso
          </Button>
        </ResponsiveFilters>
      </Paper>

      {/* Errore */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento degli impianti: {error.message}
        </Alert>
      )}

      {/* DataGrid / Mobile Cards */}
      <Paper sx={{ width: '100%' }}>
        {isMobile ? (
          <Box sx={{ p: 0.5 }}>
            {isLoading ? (
              <Stack spacing={1} sx={{ p: 1 }}>
                {[1,2,3,4,5].map(i => (
                  <Card key={i} variant="outlined"><CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ height: 60 }}><LinearProgress /></Box>
                  </CardContent></Card>
                ))}
              </Stack>
            ) : plants.length === 0 ? (
              <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">Nessun impianto trovato</Typography>
            ) : (
              <Stack spacing={1}>
                {plants.map((plant) => {
                  const percent = plant.estimated_level_percent != null ? parseFloat(plant.estimated_level_percent) : parseFloat(plant.current_level_percent) || 0;
                  const threshold = parseFloat(plant.low_level_threshold) || 15;
                  const isLow = percent <= threshold;
                  return (
                    <Card key={plant.id} variant="outlined">
                      <CardActionArea onClick={() => navigate(`/crm/impianti/${plant.id}`)}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {plant.nome || plant.plant_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {plant.plant_code} {plant.citta ? `- ${plant.citta}` : ''}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                              <Chip label={plant.attivo ? 'Attivo' : 'Inattivo'} size="small" color={plant.attivo ? 'success' : 'default'} sx={{ fontSize: '0.7rem', height: 22 }} />
                              {isLow && <WarningIcon color="error" sx={{ fontSize: 18 }} />}
                              <ChevronRightIcon color="action" sx={{ fontSize: 20 }} />
                            </Box>
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <LevelIndicator percent={percent} threshold={threshold} />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        ) : (
        <DataGrid
          rows={plants}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          sortingMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(newModel) => {
            setSortModel(newModel.length > 0 ? newModel : [{ field: 'plant_code', sort: 'asc' }]);
            setPaginationModel(prev => ({ ...prev, page: 0 }));
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          slots={{
            toolbar: () => <CustomToolbar onRefresh={refetch} />,
          }}
          onRowClick={(params) => navigate(`/crm/impianti/${params.row.id}`)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
              backgroundColor: 'action.hover',
            },
          }}
        />
        )}
      </Paper>

      {/* Dialog Creazione Rapido */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuovo Impianto</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Codice Impianto *"
                value={newPlant.plant_code}
                onChange={(e) => setNewPlant({ ...newPlant, plant_code: e.target.value })}
                helperText="Codice univoco per associare clienti e contatori"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Codice Consegna"
                value={newPlant.delivery_code}
                onChange={(e) => setNewPlant({ ...newPlant, delivery_code: e.target.value })}
                helperText="Codice per associare rifornimenti da fatture XML"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome/Descrizione"
                value={newPlant.nome}
                onChange={(e) => setNewPlant({ ...newPlant, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={newPlant.indirizzo}
                onChange={(e) => setNewPlant({ ...newPlant, indirizzo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CAP"
                value={newPlant.cap}
                onChange={(e) => setNewPlant({ ...newPlant, cap: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Città"
                value={newPlant.citta}
                onChange={(e) => setNewPlant({ ...newPlant, citta: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Provincia"
                value={newPlant.provincia}
                onChange={(e) => setNewPlant({ ...newPlant, provincia: e.target.value })}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacità Totale (litri)"
                type="number"
                value={newPlant.capacita_totale}
                onChange={(e) => setNewPlant({ ...newPlant, capacita_totale: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Livello Iniziale (litri)"
                type="number"
                value={newPlant.current_level_liters}
                onChange={(e) => setNewPlant({ ...newPlant, current_level_liters: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Soglia Allarme (%)"
                type="number"
                value={newPlant.low_level_threshold}
                onChange={(e) => setNewPlant({ ...newPlant, low_level_threshold: e.target.value })}
                helperText="Percentuale sotto cui scatta l'allarme"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate(newPlant)}
            disabled={!newPlant.plant_code || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creazione...' : 'Crea Impianto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, plant: null })} fullScreen={isMobile}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare l'impianto <strong>{deleteDialog.plant?.plant_code}</strong>?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, plant: null })}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate(deleteDialog.plant?.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlantList;
